import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { Variant } from '@material-ui/core/styles/createTypography'
import { DeleteOutlined, MoreVertOutlined } from '@material-ui/icons'
import classNames from 'classnames'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import { MoneyString } from 'model/Money'
import moment from 'moment'
import React, {
  CSSProperties,
  MouseEventHandler,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import {} from 'react-trello'
import { ProjectSummaryDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { RootAppState } from 'store/configureStore'
import { useKanbanCardStyles } from './KanbanCard.styles'
import { LaneScrollContext } from './KanbanComponents/LaneScrollContext'

type CardProps = {
  showDeleteButton?: boolean
  onDelete?: () => void
  onClick?: () => void
  style?: CSSProperties
  tagStyle?: CSSProperties
  className?: string
  id: string
  title: string
  label: string
  description: string
  tags: Array<string>
  loading: Record<string, boolean>
  setPriority: (projectId: string, priority: number) => void
  index: number
}

export const KanbanProjectSummaryCard = (props: CardProps) => {
  const classes = useKanbanCardStyles()
  const { t } = useTranslation()

  const projectSummary = JSON.parse(props.description) as ProjectSummaryDto

  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const isSellerParty = useAppSelector(
    (state: RootAppState) => !state.user?.organizationContext?.isBuyingParty
  )

  const scrollIndex = React.useContext(LaneScrollContext) || {
    min: 0,
    max: Infinity,
  }

  if (props.index < scrollIndex.min || props.index > scrollIndex.max) {
    return null
  }

  const handleMenuOpen: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()

    setMenuOpen(true)
  }

  const setPriority = (
    e: React.MouseEvent<HTMLLIElement>,
    projectId: string,
    priority: number
  ) => {
    e.preventDefault()
    e.stopPropagation()

    props.setPriority(projectId, priority)
  }

  return (
    <Card
      elevation={0}
      data-id={props.id}
      style={props.style}
      className={classNames(
        classes.root,
        props.className,
        classes[`priority-${isSellerParty ? projectSummary.priority : 0}`]
      )}
    >
      {isSellerParty && (
        <Menu
          open={menuOpen}
          anchorEl={menuButtonRef.current}
          onClose={() => setMenuOpen(false)}
        >
          <MenuItem
            onClick={(e) => {
              setPriority(e, props.id, 0)
            }}
            className={classes['priority-0']}
          >
            <Typography variant="body1">
              {t('project:priority-0', 'not defined')}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              setPriority(e, props.id, 1)
            }}
            className={classes['priority-1']}
          >
            <Typography variant="body1">
              {t('project:priority-1', 'urgent')}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              setPriority(e, props.id, 2)
            }}
            className={classes['priority-2']}
          >
            <Typography variant="body1">
              {t('project:priority-2', 'important')}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              setPriority(e, props.id, 3)
            }}
            className={classes['priority-3']}
          >
            <Typography variant="body1">
              {t('project:priority-3', 'normal')}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              setPriority(e, props.id, 4)
            }}
            className={classes['priority-4']}
          >
            <Typography variant="body1">
              {t('project:priority-4', 'low')}
            </Typography>
          </MenuItem>
        </Menu>
      )}

      <CardHeader
        className={classes.header}
        titleTypographyProps={{
          variant: 'body1',
        }}
        subheaderTypographyProps={{
          variant: 'caption',
        }}
        subheader={props.label}
        title={props.title}
        action={
          (props.showDeleteButton && (
            <IconButton onClick={props.onClick}>
              <DeleteOutlined />
            </IconButton>
          )) ||
          (isSellerParty && (
            <IconButton
              onClick={handleMenuOpen}
              ref={menuButtonRef}
              size="small"
              className={classes.headerMenu}
            >
              <MoreVertOutlined />
            </IconButton>
          ))
        }
      />
      <LoadingContainer
        loading={
          props.loading[`UpdateProjectStatus-${projectSummary.id}`] ||
          props.loading[`UpdateProjectPriority-${projectSummary.id}`]
        }
        noMessage
      >
        <CardContent className={classes.content} onClick={props.onClick}>
          <Typography variant="body2">
            {projectSummary.buyingPartyName}
          </Typography>
          <Typography variant="caption" component="p" color="inherit">
            {projectSummary.sellingPartyName}
          </Typography>
          {projectSummary.salesPerson && (
            <Typography variant="caption" component="p">
              {projectSummary.salesPerson.email}
            </Typography>
          )}
          <Divider orientation="horizontal" style={{ margin: '4px 0' }} />

          {/* <CardLabelValue
            label={t('common:priority')}
            value={t(`project:priority-${projectSummary.priority}`)}
            classes={{
              ...classes,
              value: classNames(classes.value, classes.rightAligned),
            }}
          /> */}

          <CardLabelValue
            label={t('common:num-items')}
            value={projectSummary.numberOfItems}
            classes={{
              ...classes,
              value: classNames(classes.value, classes.rightAligned),
            }}
          />
          <CardLabelValue
            label={t('common:last-operation')}
            value={moment(projectSummary.lastOperation).fromNow()}
            classes={{
              ...classes,
              value: classNames(classes.value, classes.rightAligned),
            }}
          />
          <Divider orientation="horizontal" style={{ margin: '4px 0' }} />

          <CardLabelValue
            label={t('common:net-sales-price')}
            value={MoneyString(projectSummary.salesPriceOfItems)}
            classes={{
              ...classes,
              value: classNames(classes.value, classes.rightAligned),
            }}
            valueVariant="body2"
          />
        </CardContent>
      </LoadingContainer>
    </Card>
  )
}

// const getFirstNameLastName = (person: {
//   firstName?: string
//   lastName?: string
// }) => `${person?.firstName || ''} ${person?.lastName || ''}`.trim()

function CardLabelValue({
  label,
  value,
  classes,
  valueVariant,
}: {
  label: string
  value: React.ReactText
  classes: { label: string; value: string; valueLabelRoot: string }
  valueVariant?: Variant
}) {
  return (
    <div className={classes.valueLabelRoot}>
      <div className={classes.label}>
        <Typography variant="caption" color="inherit">
          {label}:
        </Typography>
      </div>
      <div className={classes.value}>
        <Typography variant={valueVariant || 'caption'} color="inherit">
          {value}
        </Typography>
      </div>
    </div>
  )
}

export default React.memo(
  KanbanProjectSummaryCard,
  (a, b) => a.description === b.description
)
